import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const lastUpdated = "December 31, 2024";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-dark to-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          <p className="mt-2 text-white/80">Last updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Introduction</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              ACP Conference Planner, a product of Resurgence Technology Group, LLC ("we," "our," or "us"), is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our website and services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Personal Information</h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              When you create an account or use our services, we may collect:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li>Name and email address</li>
              <li>Church name and location (optional)</li>
              <li>Account credentials</li>
              <li>Profile information for speakers</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Usage Information</h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              We automatically collect certain information when you use our platform:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li>Pages and events you view</li>
              <li>Browser type and device information</li>
              <li>IP address (anonymized)</li>
              <li>Referring website</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">How We Use Your Information</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li>Provide and maintain our services</li>
              <li>Process your account registration</li>
              <li>Send you important updates about events</li>
              <li>Improve our platform and user experience</li>
              <li>Analyze usage patterns and trends</li>
              <li>Respond to your inquiries and support requests</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Information Sharing</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              We do not sell your personal information. We may share information with:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li><strong>Service Providers:</strong> Third-party services that help us operate (e.g., Supabase for database, Stripe for payments)</li>
              <li><strong>Event Organizers:</strong> If you register for an event, your registration information may be shared with the organizer</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Data Security</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your 
              personal information. However, no method of transmission over the Internet is 100% secure, 
              and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Cookies and Tracking</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We use cookies and similar technologies to enhance your experience, remember your preferences, 
              and analyze how our platform is used. You can control cookie settings through your browser.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Your Rights</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your account</li>
              <li>Opt-out of marketing communications</li>
              <li>Export your data</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Children's Privacy</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              Our services are not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Changes to This Policy</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              <a href="mailto:privacy@conferenceplanner2026.com" className="text-primary hover:underline">
                privacy@conferenceplanner2026.com
              </a>
            </p>
          </section>

        </div>

        {/* Back Link */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-primary hover:text-primary-dark font-medium inline-flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
